import React, {Component} from 'react'
import Chart from "react-apexcharts";

import {
    Icon,
    Spinner,
    Checkbox,
    Range,
    GridCell,
    Grid,
    Button,
    Theme,
    css,
    Section,
    Select,
    TextInput,
    InputGroup,
    confirm,
    FieldLabel,
} from "@bdfx/mergado-ui-kit"

import {superadminFilterQueue, superadminGetQueuesData, superadminGetTasksPerProject} from "app/helpers/api";
import {ApexOptions} from "apexcharts";
import {AxiosResponse} from "axios";
import SectionLoader from "app/components/section-loader";
import trans from "trans";
import store from "store";
import {createToast} from "actions";
import {textToast} from "app/helpers/toast_factory";
import { Slider } from '@mui/material';
import { ConfirmStyles } from 'load_styles';

interface Props {}

type DataType = {
    x: number
    y: number
}

type TasksPerProjectType = {
    name: string
    data: [{
        project_id: string
        count: number
    }]
}

type QueueDataType = {
    data: DataType[]
    name: string
}

export type QueueActionType = 'move' | 'delete' | 'count' | 'remove_duplicities'
const actions = ['move', 'delete', 'count', 'remove_duplicities']

interface State {
    isLoading: boolean
    isProcessing: boolean
    showDataLabels: boolean
    lengthsAge: number
    lengthsIncr: number
    autoRefresh: boolean
    queuesData: QueueDataType[]
    tasksPerProject: TasksPerProjectType[]
    originQueueName: string
    destinationQueueName: string
    whereKey: string
    whereValue: number
    action: QueueActionType
    byProduct: number[]
    byProject: number[]
    byImage: number[]
}


class RabbitQueues extends Component<Props, State> {
    protected checkingInterval

    constructor(props) {
        super(props)

        this.state = {
            isLoading: false,
            isProcessing: false,
            queuesData: null,
            autoRefresh: false,
            showDataLabels: true,
            lengthsIncr: 60,
            lengthsAge: 3600,
            tasksPerProject: null,
            originQueueName: null,
            destinationQueueName: null,
            whereKey: 'project_id',
            whereValue: null,
            action: 'count',
            byProduct: null,
            byProject: null,
            byImage: null,
        }
    }

    getQueuesData = () => {
        const {lengthsAge, lengthsIncr} = this.state

        this.setState({isLoading: true})

        superadminGetQueuesData(lengthsAge, lengthsIncr)
            .then((response: AxiosResponse<any>) => {
                this.setState({queuesData: response.data})
            })
            .catch(error => {
                console.log(error.response)
            })
            .finally(() => this.setState({isLoading: false}))
    }

    startChecking = () => {
        this.checkingInterval = setInterval(() => {
            this.getQueuesData()
        }, 20000)
    }

    componentDidMount() {
        this.getQueuesData()

        if (this.state.autoRefresh) {
            this.startChecking()
        }

        superadminGetTasksPerProject()
            .then((response: AxiosResponse<TasksPerProjectType[]>) => {
                this.setState({tasksPerProject: response.data})
            })
            .catch(e => {
                console.error('cannot get tasks per project', e)
            })
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        if (prevState && prevState.autoRefresh !== this.state.autoRefresh) {
            if (this.state.autoRefresh) {
                this.startChecking()
            } else {
                clearInterval(this.checkingInterval)
            }
        }
    }

    componentWillUnmount(): void {
        clearInterval(this.checkingInterval)
    }

    handleFilterQueue = () => {
        const {isProcessing, originQueueName, destinationQueueName, whereKey, whereValue, action} = this.state

        if (!isProcessing) {
            this.setState({isProcessing: true})
            superadminFilterQueue(originQueueName, destinationQueueName, whereKey, whereValue, action)
                .then((response: AxiosResponse<any>) => {
                    console.debug(originQueueName, response.data)
                    this.setState({
                        byProduct: response.data?.by_product || null,
                        byProject: response.data?.by_project || null,
                        byImage: response.data?.by_image || null,
                    })
                    store.dispatch(createToast(textToast('success', 'done', 10, <Icon type={'check'} size={28} color={Theme.green} />)))
                })
                .catch(e => {
                    store.dispatch(createToast(textToast('error', JSON.stringify(e.response?.data), 10, <Icon type={'exclamation-triangle'} size={28} color={Theme.red}/>)))
                })
                .finally(() => this.setState({isProcessing: false}))
        }
    }

    render() {
        const {isLoading, queuesData, tasksPerProject, originQueueName, destinationQueueName, whereKey, whereValue, action, isProcessing, byProduct, byProject, byImage} = this.state

        if (!(queuesData && queuesData[0] && queuesData[0].data && queuesData[0].data.length > 0)) {
            return <SectionLoader/>
        }

        const queuesOptions = queuesData.map((item: QueueDataType) => item.name).map((o: string) => {
            return {
                label: o,
                value: o,
            }
        })

        const whereKeyOptions = ['project_id', 'product_id', 'image_id'].map((o: string) => {
            return {
                label: o,
                value: o,
            }
        })

        return (
            <div className={isLoading ? 'muk-helper-loading-line with-line' : ''}>
                <Grid cols={'2fr 1fr'}>
                    <GridCell>
                        <Chart
                            options={options(queuesData, this.state.showDataLabels)}
                            series={queuesData}
                            type="area"
                            height={400}
                        />
                    </GridCell>

                    <GridCell>
                        <FieldLabel>Age</FieldLabel>
                        <Slider
                            value={this.state.lengthsAge}
                            onChange={(event, value: number) => {
                                this.setState({ lengthsAge: value })
                            }}
                            min={360}
                            max={3600}
                            marks={[{value: 720},{value: 1080},{value: 1440},{value: 1800},{value: 2160},{value: 2520}]}
                            step={60}
                            valueLabelDisplay="on"
                        />

                        <FieldLabel>Increament</FieldLabel>
                        <Slider
                            value={this.state.lengthsIncr}
                            min={1}
                            max={60}
                            step={1}
                            onChange={(event, value: number) => {
                                this.setState({ lengthsIncr: value })
                            }}
                            marks={[{value: 0},{value: 60},{value: 120},{value: 180},{value: 240},{value: 300}]}
                            valueLabelDisplay="on"
                        />

                        <Checkbox
                            value={this.state.showDataLabels}
                            label={'Show datalabels'}
                            onChange={() => {
                                this.setState({showDataLabels: !this.state.showDataLabels})
                            }}
                        />

                        <Checkbox
                            value={this.state.autoRefresh}
                            label={'Autorefresh'}
                            onChange={() => {
                                this.setState({autoRefresh: !this.state.autoRefresh})
                            }}
                        />

                        <Button
                            type={'void'}
                            disabled={isLoading}
                            color={Theme.green}
                            onClick={() => this.getQueuesData()}
                            icon={<Icon type={'refresh'} text={'Refresh now'}/>}
                            style={{marginTop: '40px'}}
                        />
                    </GridCell>
                </Grid>


                <StyledSection
                    prefix={<FormGrid cols={'auto auto'}>
                        <GridCell>Tasks from</GridCell>
                        <GridCell>
                            <Select
                                options={queuesOptions}
                                value={`${originQueueName}`}
                                onClear={() => this.setState({originQueueName: null})}
                                onChange={(value) => this.setState({originQueueName: value})}
                            />
                        </GridCell>
                    </FormGrid>}
                    header={<FormGrid cols={'auto auto auto auto'}>
                        <GridCell>where</GridCell>
                        <GridCell>
                            <Select
                                options={whereKeyOptions}
                                value={`${whereKey}`}
                                invalid={(whereValue && !whereKey)}
                                onClear={() => this.setState({whereKey: null})}
                                onChange={(value) => this.setState({whereKey: value})}
                            />
                        </GridCell>
                        <GridCell>=</GridCell>
                        <GridCell>
                            <TextInput
                                value={whereValue}
                                placeholder={'value'}
                                onChange={(value) => this.setState({whereValue: value})}
                                onClear={() => this.setState({whereValue: null})}
                            />
                        </GridCell>
                    </FormGrid>}
                    suffix={<FormGrid cols={'repeat(2, auto) max-content'}>
                        <GridCell>
                            <InputGroup
                                orientation="horizontal"
                                style={{width: '500px'}}
                                value={action}
                                singleChoice={true}
                                items={actions.map((action) => {
                                    return {value: action, label: action}
                                })}
                                onChange={(value) => this.setState({action: value})}
                            />
                        </GridCell>
                        <GridCell>
                            {action === 'move' &&
                            <TextInput
                                value={destinationQueueName}
                                placeholder={'destination queue'}
                                title={'destination queue name'}
                                onChange={(value) => this.setState({destinationQueueName: value})}
                                onClear={() => this.setState({destinationQueueName: null})}
                            />
                            }
                        </GridCell>
                        <GridCell>
                            {originQueueName &&
                            <Button
                                type={'void'}
                                inactive={isProcessing}
                                color={isProcessing ? Theme.grey : Theme.steelblue}
                                icon={<Icon type={isProcessing ? 'spinner' : 'check'} text={'go'}/>}
                                onClick={() => confirm({
                                    title: <em>Are you sure?</em>,
                                    message: (close) => <div>Process <span className={'framed'}>{action}</span> on <span className={'framed'}>{originQueueName}</span></div>,
                                    style: ConfirmStyles,
                                    buttons: [
                                        <Button
                                            key={'confirm-yes'}
                                            icon={<Icon type={'check-slim'} text={trans("yes")}/>}
                                            color='green'
                                            type='void'
                                            size={'small'}
                                            style={{margin: '0 20px'}}
                                            onClick={() => {
                                                this.handleFilterQueue()
                                            }}/>,
                                        <Button
                                            key={'confirm-no'}
                                            icon={<Icon type={'close-light'} text={trans("no")}/>}
                                            color='red'
                                            type='void'
                                            size={'small'}
                                            style={{margin: '0 20px'}}
                                            onClick={() => close()}/>
                                    ]
                                })}
                            />
                            }
                        </GridCell>
                    </FormGrid>}>

                    <Grid cols={'1fr 1fr 1fr'}>
                        <GridCell>
                            <span className={'framed'}>by project</span>
                            {byProject &&
                            <Grid>
                                {Object.keys(byProject).map((k) => <GridCell key={k}>{k}: {byProject[k]}</GridCell>)}
                            </Grid>
                            }
                        </GridCell>

                        <GridCell>
                            <span className={'framed'}>by product</span>
                            {byProduct &&
                            <Grid style={{maxHeight: '200px', overflowY: 'auto'}}>
                                {Object.keys(byProduct).map((k) => <GridCell key={k}>{k}: {byProduct[k]}</GridCell>)}
                            </Grid>
                            }
                        </GridCell>

                        <GridCell>
                            <span className={'framed'}>by image</span>
                            {byImage &&
                            <Grid style={{maxHeight: '200px', overflowY: 'auto'}}>
                                {Object.keys(byImage).map((k) => <GridCell key={k}>{k}: {byImage[k]}</GridCell>)}
                            </Grid>
                            }
                        </GridCell>
                    </Grid>
                </StyledSection>

                <Section header={'from redis count'} color={Theme.steelblue}>
                    <StyledTasksPerProjectGrid>
                        {tasksPerProject &&
                        tasksPerProject.map(t => <React.Fragment key={t.name}>
                            <GridCell><span className={'framed'}>{t.name}</span></GridCell>
                            <GridCell>{t.data.map((item) => <div
                                key={item.project_id}>{item.project_id}: {item.count}</div>)}</GridCell>
                        </React.Fragment>)
                        }
                    </StyledTasksPerProjectGrid>
                </Section>

            </div>
        )
    }
}

const StyledSection = css(Section)({
    ' .muk-grid.m-header': {
        gridTemplateColumns: 'max-content max-content auto',
    },
})

const FormGrid = css(Grid)({
    gridGap: '10px',
    ' .muk-gridcell': {
        alignSelf: 'center',
        textAlign: 'center'
    },
    ' .muk-form-group': {
        marginBottom: 0
    },
    ' .react-select-item-option': {
        textAlign: 'left',
    },
    ' .react-select-item-container, .muk-textinput': {
        minWidth: '140px',
    }
})

const options = (series, showDatalabels) : ApexOptions => {
    return {
        series,
        labels: series.map(item => item.name),
        xaxis: {
            type: 'datetime',
            tickAmount: 6,
            labels: {
                formatter: (value, timestamp) => {
                    return new Date(timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', hour12: false}) // The formatter function overrides format property
                },
            }
        },
        yaxis: {
            title: {
                text: 'tasks'
            },
        },
        tooltip: {
            x: {
                format: 'HH:mm'
            }
        },
        chart: {
            height: 350,
            type: 'line',
            id: 'rabbitmq-queue',
            width: '200px',
        },
        dataLabels: {
            enabled: showDatalabels,
        },
        stroke: {
            curve: 'smooth'
        },
        grid: {
            borderColor: '#e7e7e7',
            row: {
                colors: ['#f3f3f3', 'transparent'], // takes an array which will be repeated on columns
                opacity: 0.5
            },
        },
        markers: {
            size: 1
        },
    }
}

const StyledTasksPerProjectGrid = css(Grid)({
    gridTemplateColumns: 'max-content auto',
    gridGap: '10px',
    ' .muk-gridcell': {
        alignSelf: 'center',
    },
})

export default RabbitQueues